/**
 * Dashboard Component (Refactored)
 * =================================
 *
 * Modern dashboard with Befret-Figma design
 * Uses unified shipments collection and reactive patterns
 *
 * @version 2.0.0
 * @author BeFret Development Team
 */

import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, tap, catchError } from 'rxjs/operators';
import { DashboardService } from '../services/dashboard.service';
import { AuthService } from '../core/services/auth.service';
import { UserProfile } from '../core/models/user-profile';
import {
  DashboardStats,
  QuickAction,
  CountryRevenue,
  MonthlyRevenue,
  CHART_COLORS
} from '../models/dashboard.models';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../shared/shared.module';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-parcel-description',
    templateUrl: './parcel-description.component.html',
    styleUrls: ['./parcel-description.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, RouterModule, SharedModule, MatIconModule, MatProgressSpinnerModule, MatCardModule, MatButtonModule]
})
export class ParcelDescriptionComponent implements OnInit, OnDestroy {

  // User Profile
  userProfile?: UserProfile | null = null;
  userId?: string;

  // Dashboard Data (Observables)
  stats$!: Observable<DashboardStats>;
  stats: DashboardStats | null = null;

  // Quick Actions
  quickActions: QuickAction[] = [];

  // UI State
  isLoading = true;
  errorMessage: string | null = null;

  // Chart Colors
  chartColors = CHART_COLORS;

  // Lifecycle
  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('🎯 [DASHBOARD] Initializing dashboard (refactored version)');

    // Load user profile
    this.authService.getUser().subscribe(user => {
      if (user) {
        this.userId = user.uid;
        this.authService.getUserProfile(user.uid).then(profile => {
          this.userProfile = profile;
          console.log('👤 [DASHBOARD] User profile loaded:', profile?.fullname);
          this.cdr.markForCheck();
        });
      }
    });

    // Load dashboard data
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load dashboard statistics and metrics
   */
  private loadDashboardData(): void {
    console.log('📊 [DASHBOARD] Loading dashboard statistics...');

    this.stats$ = this.dashboardService.getDashboardStats().pipe(
      tap(stats => {
        console.log('✅ [DASHBOARD] Stats loaded:', stats);
        this.stats = stats;
        this.isLoading = false;

        // Generate quick actions with badges
        this.quickActions = this.dashboardService.getQuickActions(stats);
        console.log('⚡ [DASHBOARD] Quick actions generated:', this.quickActions.length);

        this.cdr.markForCheck();
      }),
      catchError(error => {
        console.error('❌ [DASHBOARD] Error loading stats:', error);
        this.errorMessage = 'Erreur lors du chargement des statistiques';
        this.isLoading = false;
        this.cdr.markForCheck();
        return of({
          totalShipments: 0,
          totalRevenue: 0,
          averageShipmentValue: 0,
          byStatus: [],
          byCountry: [],
          byMonth: [],
          recentShipments: [],
          topDestinations: []
        } as DashboardStats);
      }),
      takeUntil(this.destroy$)
    );

    // Subscribe to trigger loading
    this.stats$.subscribe();
  }

  /**
   * Refresh dashboard data
   */
  refreshData(): void {
    console.log('🔄 [DASHBOARD] Refreshing data...');
    this.isLoading = true;
    this.cdr.markForCheck();
    this.loadDashboardData();
  }

  /**
   * Navigate to parcel list
   */
  navigateToParcelList(): void {
    this.router.navigate(['/parcel-list']);
  }

  parcelList(): void {
    this.navigateToParcelList();
  }

  /**
   * Navigate to filtered list by status
   */
  navigateToStatus(status: string): void {
    this.router.navigate(['/parcel-list'], { queryParams: { status } });
  }

  /**
   * Navigate to filtered list by country
   */
  navigateToCountry(countryCode: string): void {
    this.router.navigate(['/parcel-list'], { queryParams: { country: countryCode } });
  }

  countryList(country: string): void {
    this.navigateToCountry(country);
  }

  /**
   * Navigate using quick action
   */
  navigateWithAction(action: QuickAction): void {
    console.log('🎯 [DASHBOARD] Quick action clicked:', action.id);
    this.router.navigate([action.route], { queryParams: action.queryParams });
  }

  // Legacy navigation methods (for backward compatibility with old template)
  deliveredList(delivered: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { delivered } });
  }

  packageList(paquet: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { paquet } });
  }

  pendingList(pending: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { pending } });
  }

  arrivedList(arrived: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { arrived } });
  }

  sentList(sent: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { sent } });
  }

  receivedList(received: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { received } });
  }

  mailList(mail: any): void {
    this.router.navigate(['parcel-list'], { queryParams: { mail } });
  }

  /**
   * Get gradient for monthly bars
   */
  getBarGradient(index: number, total: number): string {
    const colorIndex = index % CHART_COLORS.length;
    const color = CHART_COLORS[colorIndex];

    // Create gradient from color to lighter version
    return `linear-gradient(135deg, ${color} 0%, ${this.lightenColor(color, 20)} 100%)`;
  }

  /**
   * Lighten a hex color by percentage
   */
  private lightenColor(hex: string, percent: number): string {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Lighten
    const lighten = (c: number) => Math.min(255, Math.floor(c + (255 - c) * (percent / 100)));

    const newR = lighten(r);
    const newG = lighten(g);
    const newB = lighten(b);

    // Convert back to hex
    const toHex = (n: number) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  }

  /**
   * Get scale markers for monthly chart
   */
  getScaleMarkers(data: MonthlyRevenue[]): { value: number; position: number }[] {
    if (!data || data.length === 0) return [];

    const maxValue = Math.max(...data.map(m => m.revenue));
    const markers: { value: number; position: number }[] = [];

    // Generate 5 markers from 0 to max
    for (let i = 0; i <= 4; i++) {
      const value = Math.round((maxValue / 4) * i);
      const position = (value / maxValue) * 100;
      markers.push({ value, position });
    }

    return markers;
  }

  /**
   * Format currency
   */
  formatCurrency(value: number): string {
    return this.dashboardService.formatCurrency(value);
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number): string {
    return this.dashboardService.formatPercentage(value);
  }

  /**
   * Get status color class
   */
  getStatusColorClass(index: number): string {
    return `f-color-${index % 5}`;
  }

  /**
   * Get country bar color class
   */
  getCountryBarColorClass(index: number): string {
    return `color-${index % 5}`;
  }

  /**
   * Check if user has manager privileges
   */
  isManager(): boolean {
    return this.userProfile?.ability === 'topManager' || this.userProfile?.ability === 'manager';
  }

  /**
   * Get total count for quick display
   */
  getTotalCount(): number {
    return this.stats?.totalShipments || 0;
  }

  get totalCount(): number {
    return this.getTotalCount();
  }

  /**
   * Get total revenue for quick display
   */
  getTotalRevenue(): number {
    return this.stats?.totalRevenue || 0;
  }

  /**
   * Get shipments by status count
   */
  getStatusCount(statusName: string): number {
    if (!this.stats) return 0;
    return this.dashboardService.getStatusCount(this.stats, statusName);
  }

  /**
   * Get pending count (for Belgium section)
   * Inclut les nouveaux et anciens noms de statuts pour compatibilité
   */
  get pendingCount(): number {
    return this.getStatusCount('order') + this.getStatusCount('pending');
  }

  /**
   * Get received count (for Belgium section)
   * Inclut les nouveaux et anciens noms de statuts pour compatibilité
   */
  get receivedCount(): number {
    return this.getStatusCount('warehouse') + this.getStatusCount('collected_europe');
  }

  /**
   * Get sent count (for DRC section)
   * Inclut les nouveaux et anciens noms de statuts pour compatibilité
   */
  get sentCount(): number {
    return this.getStatusCount('befret_transit') + this.getStatusCount('in_transit');
  }

  /**
   * Get arrived count (for DRC section)
   * Inclut les nouveaux et anciens noms de statuts pour compatibilité
   */
  get arrivedCount(): number {
    return this.getStatusCount('congo_distribution') + this.getStatusCount('out_for_delivery');
  }

  /**
   * Get delivered count
   */
  get deliveredCount(): number {
    return this.getStatusCount('delivered');
  }

  /**
   * Get country summary (for table display)
   */
  get countrySummary(): CountryRevenue[] {
    return this.stats?.byCountry || [];
  }

  /**
   * Get monthly data (for chart display)
   */
  get monthlyData(): MonthlyRevenue[] {
    return this.stats?.byMonth || [];
  }

  /**
   * Get total cost (for percentage calculations)
   */
  get totalCost(): number {
    return this.stats?.totalRevenue || 0;
  }

  /**
   * Calculate scale markers (for monthly chart)
   */
  get scaleMarkers(): { value: number; position: number }[] {
    return this.getScaleMarkers(this.monthlyData);
  }

  // Math utility for templates
  Math = Math;
}
